import Link from 'next/link';
import router, { useRouter } from 'next/router';
import React, { useMemo } from 'react';

import HelpIcon from '@app/components/icons/HelpIcon';
import LogoutIcon from '@app/components/icons/LogoutIcon';
import { useAuth } from '@app/hooks/useAuth';
import { NavigationItem } from '@app/hooks/useCommonData';
import { useDashboard } from '@app/hooks/useDashboard';
import { useWindow } from '@app/hooks/useWindow';
import { cn } from '@app/libs/utils';

import ArrowDownIcon from '../icons/ArrowDownIcon';
import LogoImage from '../LogoImage';

import Avatar from './Avatar';

interface SideMenuComponentProps {
  className?: string;
  sidebar?: NavigationItem[];
}

const SideMenuComponent: React.FC<SideMenuComponentProps> = ({
  className,
  sidebar: customSidebar,
}) => {
  const { info: user, logout } = useAuth();
  const { screenSmallerThan } = useWindow();
  const { sidebar, collapseSidebar, setCollapseSidebar } = useDashboard();
  const { pathname } = useRouter();

  const checkPathIsActive = (path: string) => {
    if (pathname === path) return true;
    if (path !== '/dashboard' && pathname.startsWith(path)) return true;

    return false;
  };

  return (
    <div
      className={cn(
        'bg-white rounded-lg shadow-sm h-full flex flex-col gap-4 border border-gray-200 overflow-hidden',
        'z-9999',
        className,
        collapseSidebar ? 'w-16' : 'md:w-64 w-10'
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center md:p-3 p-2 border-b border-gray-200',
          'relative'
        )}
      >
        <Link href="/">
          <LogoImage
            variant={collapseSidebar || screenSmallerThan('md') ? 'icon' : 'full'}
            size="md"
            theme="light"
          />
        </Link>
      </div>
      <div className="flex flex-col items-center gap-5 md:p-7 sm:p-1">
        <Avatar className="w-23 bg-gray-200 rounded-full overflow-hidden max-w-full aspect-square" />
        <div className="max-w-full text-center overflow-hidden">
          <h3 className="w-full text-lg font-bold text-black text-truncated text-ellipsis md:block hidden">
            {user?.alias || user?.fullName || user?.email}
          </h3>
          <p className="text-sm text-gray-500 text-truncated text-ellipsis">{user?.role}</p>
        </div>
      </div>

      <div className="px-1 md:px-4 pb-1 md:pb-8 flex flex-col grow gap-4 text-sm shrink-1 overflow-y-auto">
        <div className="flex flex-col gap-4 grow shrink">
          {(customSidebar ?? sidebar)?.map(item => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                'flex items-center gap-4 md:p-3 p-1 text-gray-500 rounded-lg cursor-pointer transition-colors duration-200 relative',
                "before:content-[''] before:absolute before:left-0 before:top-0 before:w-full before:h-full before:bg-gray-100 before:rounded-lg before:z-0",
                'before:transition-all before:duration-200',
                'before:w-0 hover:before:w-full',
                checkPathIsActive(item.href) ? '!text-white before:!bg-blue-800 before:w-full' : ''
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-center relative z-1',
                  collapseSidebar || screenSmallerThan('md') ? 'w-5 h-5' : 'w-6 h-6'
                )}
              >
                {item.icon}
              </div>
              <span className="relative z-1 md:block hidden">{item.label}</span>
            </Link>
          ))}
        </div>

        <div className="shrink flex flex-col justify-end gap-4">
          <div
            className={cn(
              'flex items-center gap-4 py-1 md:py-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-all duration-200',
              collapseSidebar || screenSmallerThan('md') ? 'px-1' : 'px-3'
            )}
          >
            <div className="">
              <HelpIcon
                size={collapseSidebar || screenSmallerThan('md') ? 20 : 24}
                className="text-gray-500"
              />
            </div>
            <span className="text-gray-500 md:block hidden">Trợ giúp & Hỗ trợ</span>
          </div>

          <div
            className={cn(
              'flex items-center gap-4 py-1 md:py-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-all duration-200',
              collapseSidebar || screenSmallerThan('md') ? 'px-1' : 'px-3'
            )}
            onClick={() => {
              logout(false);
              router.push('/');
            }}
          >
            <div className="w-5 md:w-6 h-5 md:h-6 flex items-center justify-center text-red-500">
              <LogoutIcon size={24} />
            </div>
            <span className="text-red-500 md:block hidden">Đăng xuất</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SideMenuComponent;
